import { ref, computed } from 'vue';
import type { CollectionItemInterface } from '@/store/useCollectionsStore';
import { useCollectionsStore } from '@/store/useCollectionsStore';
import { COLLECTION_SELECTION, type CollectionSelection } from '../constants/constants';

export const useRequestContentCollectionSelection = (initialCollectionId?: string | null) => {
  const collectionsStore = useCollectionsStore();

  // Collection state
  const selectedCollection = ref<CollectionItemInterface | null>(null);
  const collectionSelection = ref<CollectionSelection>(COLLECTION_SELECTION.AUTO);

  // Initialize collection if provided
  const initializeCollection = async (collectionId?: string | null) => {
    if (!collectionId) {
      selectedCollection.value = null;
      collectionSelection.value = COLLECTION_SELECTION.AUTO;
      return;
    }

    // Ensure collections are loaded
    if (collectionsStore.list.length === 0) {
      await collectionsStore.load();
    }

    // Find the collection
    const collection = collectionsStore.list.find(c => c.id === collectionId);
    if (collection) {
      selectedCollection.value = collection;
      collectionSelection.value = COLLECTION_SELECTION.MANUAL;
    } else {
      selectedCollection.value = null;
      collectionSelection.value = COLLECTION_SELECTION.AUTO;
    }
  };

  // Collection select options
  const collectionSelectOptions = computed(() => {
    const options = [{ label: 'Auto created collection', value: COLLECTION_SELECTION.AUTO }];

    // Add selected collection as option if one exists
    if (selectedCollection.value) {
      options.push({
        label: selectedCollection.value.name,
        value: COLLECTION_SELECTION.MANUAL,
      });
    }

    options.push({ label: 'Pick manually', value: COLLECTION_SELECTION.PICK });

    return options;
  });

  // Handle collection selection change
  const handleCollectionSelectionChange = (value: string, onShowPicker: () => void) => {
    if (value === COLLECTION_SELECTION.PICK) {
      onShowPicker();
      return;
    }

    if (value === COLLECTION_SELECTION.AUTO) {
      selectedCollection.value = null;
      collectionSelection.value = COLLECTION_SELECTION.AUTO;
      return;
    }

    // MANUAL selection - keep current collection if available
    if (value === COLLECTION_SELECTION.MANUAL && !selectedCollection.value) {
      // Reset if no collection available
      collectionSelection.value = COLLECTION_SELECTION.AUTO;
    }
  };

  // Update collection from picker
  const updateCollectionFromPicker = (collection: CollectionItemInterface) => {
    selectedCollection.value = collection;
    collectionSelection.value = COLLECTION_SELECTION.MANUAL;
  };

  // Get collection ID for saving
  const getCollectionIdForSaving = (): string | null => {
    if (collectionSelection.value === COLLECTION_SELECTION.MANUAL && selectedCollection.value) {
      return selectedCollection.value.id;
    }
    return null;
  };

  // Initialize on creation
  if (initialCollectionId) {
    initializeCollection(initialCollectionId);
  }

  return {
    // Collection state
    selectedCollection,
    collectionSelection,
    collectionSelectOptions,

    // Collection methods
    initializeCollection,
    handleCollectionSelectionChange,
    updateCollectionFromPicker,
    getCollectionIdForSaving,
  };
};
